/**
 * The LexiStar display rules API (ST-00276 phase 2: Settings > LexiStar > Map colours & states).
 * Shapes: `types/lexistar-display-rules`. Server: server/modules/lexistar/display, mounted at
 * /api/lexistar.
 *
 * Thin wrapper over the shared axios instance in `services/api` (cookie-session auth, CSRF
 * header, single-flight 401 refresh; `api` already carries `/api`). Resolves to the response BODY.
 *
 *   getDisplayRules(q)            → the rules in force, the defaults, the legend, can_manage
 *   saveDisplayRules(input)       → the saved set + changed   (409 code stale_version: reload)
 *   resetDisplayRules(input)      → back to the code defaults (409 code stale_version)
 *   getDisplayCatalogue(q)        → the fields and which the organisation's devices report
 *   getDisplayHistory(q)          → every version, who and when
 *   getDisplayVersion(v, q)       → one version's rules
 *   getDisplaySpans(from, to, q)  → the rule sets in force over a window (Replay, Trips)
 *   getDisplayPreview(q)          → the organisation's trucks now: facts, events, display
 */
import api from './api';
import type {
  DisplayCatalogue,
  DisplayPreview,
  DisplayRuleHistory,
  DisplayRuleSet,
  DisplayRuleSpans,
  DisplayRulesInput,
  DisplayRulesResetInput,
  DisplayRulesSaved,
  DisplayRuleVersionDetail,
} from '../types/lexistar-display-rules';

const BASE = '/lexistar/display-rules';

interface TenantQuery {
  tenant_id?: number;
}

const params = (q: Record<string, unknown>): Record<string, string> =>
  Object.fromEntries(
    Object.entries(q)
      .filter(([, v]) => v !== undefined && v !== null && v !== '')
      .map(([k, v]) => [k, String(v)])
  );

export async function getDisplayRules(q: TenantQuery = {}): Promise<DisplayRuleSet> {
  return (await api.get(BASE, { params: params({ ...q }) })).data as DisplayRuleSet;
}

export async function saveDisplayRules(input: DisplayRulesInput): Promise<DisplayRulesSaved> {
  return (await api.put(BASE, input)).data as DisplayRulesSaved;
}

export async function resetDisplayRules(input: DisplayRulesResetInput): Promise<DisplayRulesSaved> {
  return (await api.post(`${BASE}:reset`, input)).data as DisplayRulesSaved;
}

export async function getDisplayCatalogue(q: TenantQuery = {}): Promise<DisplayCatalogue> {
  return (await api.get(`${BASE}/catalogue`, { params: params({ ...q }) })).data as DisplayCatalogue;
}

export async function getDisplayHistory(q: TenantQuery & { limit?: number } = {}): Promise<DisplayRuleHistory> {
  return (await api.get(`${BASE}/history`, { params: params({ ...q }) })).data as DisplayRuleHistory;
}

export async function getDisplayVersion(version: number, q: TenantQuery = {}): Promise<DisplayRuleVersionDetail> {
  return (await api.get(`${BASE}/history/${encodeURIComponent(String(version))}`, { params: params({ ...q }) }))
    .data as DisplayRuleVersionDetail;
}

/** `asset_keys`: the replayed truck's keys — the answer then carries its events before `from`. */
export async function getDisplaySpans(
  from: string,
  to: string,
  q: TenantQuery & { asset_keys?: readonly string[] } = {}
): Promise<DisplayRuleSpans> {
  const { asset_keys: keys, ...rest } = q;
  return (
    await api.get(`${BASE}/versions`, {
      params: params({ from, to, ...rest, asset_keys: keys && keys.length ? keys.join(',') : undefined }),
    })
  ).data as DisplayRuleSpans;
}

export async function getDisplayPreview(q: TenantQuery = {}): Promise<DisplayPreview> {
  return (await api.get(`${BASE}/preview`, { params: params({ ...q }) })).data as DisplayPreview;
}

/** The API's error code (`stale_version`, …) of a failed call, if any. */
export function displayErrorCode(err: unknown): string | null {
  const data = (err as { response?: { data?: { code?: unknown } } })?.response?.data;
  return data && typeof data.code === 'string' ? data.code : null;
}

/** The API's words for a failed call (with the rule problems, when it lists them). */
export function displayErrorText(err: unknown, fallback: string): string {
  const data = (err as { response?: { data?: { error?: unknown; message?: unknown; details?: unknown } } })?.response?.data;
  const details = Array.isArray(data?.details) ? (data!.details as unknown[]).map(String) : [];
  const head = typeof data?.error === 'string' ? data.error : typeof data?.message === 'string' ? data.message : null;
  return [head, ...details].filter(Boolean).join(' — ') || (err as Error)?.message || fallback;
}
